import React, { useState, useEffect, useMemo } from 'react';
import {
  LinkIcon, EyeIcon, EyeSlashIcon, ClipboardDocumentIcon, ArrowTopRightOnSquareIcon,
  EnvelopeIcon, PaperAirplaneIcon, CheckCircleIcon, InformationCircleIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { Switch } from '../common/Switch';
import { Quotation } from '../../types/quotation';
import { useLanguage } from '../../contexts/LanguageContext';
import { useQuotations } from '../../hooks/useQuotations';
import { useCustomers } from '../../hooks/useCustomers';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useAuth } from '../../contexts/AuthContext';
import { emailService } from '../../services/emailService';
import { customerDisplayName } from '../../utils/customerName';
import { fmtMoney, quotationTotal, fmtDate, toDate } from './quotationStatus';
import toast from 'react-hot-toast';

interface ShareQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: Quotation | null;
}

type Tab = 'email' | 'link';

const fmtDateTime = (v: any) => {
  const d = toDate(v);
  return d ? `${d.toLocaleDateString('hu-HU')} ${d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}` : '';
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const ShareQuotationModal: React.FC<ShareQuotationModalProps> = ({ isOpen, onClose, quotation }) => {
  const { t } = useLanguage();
  const { updateQuotation } = useQuotations();
  const { customers } = useCustomers();
  const { organizations } = useOrganizations();
  const { user } = useAuth();

  const customer = useMemo(() => customers?.find(c => c.id === quotation?.customerId), [customers, quotation]);
  const organization = useMemo(() => organizations?.find(o => o.id === quotation?.organizationId), [organizations, quotation]);

  const [tab, setTab] = useState<Tab>('email');

  // Link beállítások
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // E-mail
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includePassword, setIncludePassword] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sentInfo, setSentInfo] = useState<{ to: string; at: Date } | null>(null);

  const number = quotation?.quotationNumber || quotation?.number || '';

  const buildLink = (q: Quotation, shareId?: string) =>
    `${window.location.origin}/quotations/public/${q.id}${shareId ? `?share=${shareId}` : ''}`;

  useEffect(() => {
    if (!isOpen || !quotation) return;
    const ss = quotation.shareSettings;
    setIsPasswordProtected(!!ss?.isPasswordProtected);
    setPassword(ss?.password || '');
    setShowPassword(false);
    setShareLink(ss ? buildLink(quotation, ss.shareId) : '');
    const vu = toDate(ss?.validUntil);
    setValidUntil(vu ? new Date(vu.getTime() - vu.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
    setSentInfo(ss?.sentTo && ss?.sentAt ? { to: ss.sentTo, at: toDate(ss.sentAt) || new Date() } : null);
    setIncludePassword(false);

    const email = (customer as any)?.email || '';
    setTo(email);
    const orgName = organization?.name || 'YouCRM';
    setSubject(`Árajánlat – ${number}${orgName ? ` (${orgName})` : ''}`);
    const greet = customer ? `Tisztelt ${customerDisplayName(customer)}!` : 'Tisztelt Ügyfelünk!';
    setMessage(
      `${greet}\n\nMellékelten küldjük ${number} számú árajánlatunkat, amelyet az alábbi gombra kattintva tekinthet meg online.` +
      (quotation.validUntil ? `\n\nAz ajánlat érvényes: ${fmtDate(quotation.validUntil)}.` : '') +
      `\n\nKérdés esetén állunk rendelkezésére.\n\nÜdvözlettel,\n${(user as any)?.displayName || (user as any)?.name || orgName}`
    );
    setTab('email');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quotation?.id, customer?.id, organization?.id]);

  if (!quotation) return null;

  /** Link létrehozása / frissítése – visszaadja a friss beállításokat és linket */
  const saveShareSettings = async (): Promise<{ link: string; settings: any }> => {
    const settings = {
      shareId: quotation.shareSettings?.shareId || crypto.randomUUID(),
      isPasswordProtected,
      password: isPasswordProtected ? password : null,
      validUntil: validUntil ? new Date(validUntil) : null,
      createdAt: quotation.shareSettings?.createdAt || new Date(),
      viewCount: quotation.shareSettings?.viewCount || 0,
      lastViewed: quotation.shareSettings?.lastViewed || null,
      sentTo: quotation.shareSettings?.sentTo || null,
      sentAt: quotation.shareSettings?.sentAt || null,
      sentCount: quotation.shareSettings?.sentCount || 0
    };
    const publicToken = quotation.publicToken || crypto.randomUUID();
    await updateQuotation(quotation.id, { ...quotation, publicToken, shareSettings: settings });
    quotation.shareSettings = settings as any;
    quotation.publicToken = publicToken;
    const link = buildLink(quotation, settings.shareId);
    setShareLink(link);
    return { link, settings };
  };

  const handleSaveLink = async () => {
    if (isPasswordProtected && !password) { toast.error('Adjon meg jelszót, vagy kapcsolja ki a jelszavas védelmet'); return; }
    try {
      setIsSaving(true);
      await saveShareSettings();
      toast.success(quotation.shareSettings ? 'Megosztási link frissítve' : 'Megosztási link létrehozva');
    } catch (e) {
      console.error(e);
      toast.error('Hiba a megosztási link mentésekor');
    } finally {
      setIsSaving(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success('Link a vágólapra másolva');
    } catch {
      toast.error('Nem sikerült a vágólapra másolni');
    }
  };

  const handleSend = async () => {
    const email = to.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Érvényes e-mail címet adjon meg'); return; }
    if (isPasswordProtected && !password) { toast.error('Jelszavas védelemhez adjon meg jelszót (Link fül)'); return; }
    try {
      setIsSending(true);
      const { link, settings } = await saveShareSettings();
      let html = escapeHtml(message).replace(/\n/g, '<br>');
      if (isPasswordProtected && includePassword) {
        html += `<br><br><strong>Az ajánlat megnyitásához szükséges jelszó:</strong> ${escapeHtml(password)}`;
      } else if (isPasswordProtected) {
        html += `<br><br><em>Az ajánlat jelszóval védett – a jelszót külön üzenetben küldjük.</em>`;
      }
      html += `<br><br>Közvetlen link: <a href="${link}">${link}</a>`;

      await emailService.sendNotification({
        to: email,
        subject,
        message: html,
        organizationId: quotation.organizationId,
        organizationName: organization?.name,
        fromName: organization?.name,
        actionUrl: link,
        actionText: 'Árajánlat megtekintése'
      });

      const now = new Date();
      const newSettings = { ...settings, sentTo: email, sentAt: now, sentCount: (settings.sentCount || 0) + 1 };
      const newStatus = quotation.status === 'draft' || quotation.status === 'editing' ? 'sent' : quotation.status;
      await updateQuotation(quotation.id, { ...quotation, status: newStatus, shareSettings: newSettings });
      quotation.shareSettings = newSettings as any;
      quotation.status = newStatus;
      setSentInfo({ to: email, at: now });
      toast.success(`Árajánlat elküldve: ${email}`);
    } catch (e: any) {
      console.error('Quotation e-mail failed', e);
      toast.error(e?.message ? `Küldés sikertelen: ${e.message}` : 'Küldés sikertelen');
    } finally {
      setIsSending(false);
    }
  };

  const inputCls = 'w-full h-9 border rounded-lg px-3 text-sm';
  const ss = quotation.shareSettings;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Árajánlat küldése / megosztása" size="2xl" noPadding>
      <div className="flex flex-col min-h-full">
        {/* Fejléc */}
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{number}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {customer ? customerDisplayName(customer) : '–'}{organization ? ` · ${organization.name}` : ''}
              </div>
            </div>
            <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-50 whitespace-nowrap">{fmtMoney(quotationTotal(quotation), quotation.currency || 'HUF')}</div>
          </div>
          <div className="mt-3 flex items-center rounded-lg border border-[var(--crm-border)] bg-[var(--crm-bg)] p-0.5 w-full sm:w-auto sm:inline-flex">
            {([['email', 'E-mail küldés', EnvelopeIcon], ['link', 'Megosztási link', LinkIcon]] as [Tab, string, React.ElementType][]).map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 h-8 rounded-md text-sm font-medium transition-colors ${tab === k ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
              >
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 p-4 sm:p-6 space-y-4">
          {tab === 'email' ? (
            <>
              {sentInfo && (
                <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300">
                  <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Utoljára elküldve: <strong>{sentInfo.to}</strong> · {fmtDateTime(sentInfo.at)}{ss?.sentCount ? ` · összesen ${ss.sentCount}×` : ''}</span>
                </div>
              )}
              {!(customer as any)?.email && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                  <InformationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Az ügyfélhez nincs e-mail cím rögzítve – adja meg kézzel.</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Címzett</label>
                <input type="email" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} placeholder="ugyfel@ceg.hu" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Tárgy</label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Üzenet</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} className="w-full border rounded-lg px-3 py-2 text-sm" />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Az e-mailbe automatikusan bekerül az „Árajánlat megtekintése” gomb és a megosztási link.
                </p>
              </div>
              <div className="rounded-lg border border-[var(--crm-border)] px-3 py-2.5 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs">
                    <div className="font-medium text-gray-800 dark:text-gray-200">Jelszavas védelem</div>
                    <div className="text-gray-500 dark:text-gray-400">{isPasswordProtected ? 'Az ügyfélnek jelszó kell a megnyitáshoz' : 'A link jelszó nélkül megnyitható'}</div>
                  </div>
                  <Switch checked={isPasswordProtected} onChange={setIsPasswordProtected} size="sm" />
                </div>
                {isPasswordProtected && (
                  <>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + ' pr-9'} placeholder="Jelszó" />
                      <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 right-0 px-2.5 text-gray-400 hover:text-gray-700">
                        {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                      <Switch checked={includePassword} onChange={setIncludePassword} size="sm" />
                      Jelszó beleírása az e-mailbe <span className="text-gray-400">(biztonságosabb külön csatornán küldeni)</span>
                    </label>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {shareLink ? (
                <div className="rounded-lg border border-[var(--crm-border)] bg-[var(--crm-bg)] p-3">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Nyilvános link</div>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={shareLink} className="flex-1 h-9 bg-transparent border-0 text-sm text-gray-800 dark:text-gray-200 focus:ring-0 px-0 min-w-0" />
                    <button type="button" onClick={copyLink} className="crm-btn crm-btn-secondary h-8 px-2.5" title="Másolás"><ClipboardDocumentIcon className="w-4 h-4" /></button>
                    <a href={shareLink} target="_blank" rel="noopener noreferrer" className="crm-btn crm-btn-secondary h-8 px-2.5" title="Megnyitás"><ArrowTopRightOnSquareIcon className="w-4 h-4" /></a>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1"><EyeIcon className="w-3.5 h-3.5" />{ss?.viewCount || 0}× megnyitva</span>
                    {ss?.lastViewed && <span>Utoljára: {fmtDateTime(ss.lastViewed)}</span>}
                    {ss?.validUntil && <span>Link érvényes: {fmtDateTime(ss.validUntil)}</span>}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-[var(--crm-border)] p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                  Még nincs megosztási link. Állítsa be az opciókat, majd hozza létre.
                </div>
              )}

              <div className="rounded-lg border border-[var(--crm-border)] px-3 py-2.5 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs">
                    <div className="font-medium text-gray-800 dark:text-gray-200">Jelszavas védelem</div>
                    <div className="text-gray-500 dark:text-gray-400">Csak jelszóval nyitható meg</div>
                  </div>
                  <Switch checked={isPasswordProtected} onChange={setIsPasswordProtected} size="sm" />
                </div>
                {isPasswordProtected && (
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + ' pr-9'} placeholder="Jelszó" />
                    <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 right-0 px-2.5 text-gray-400 hover:text-gray-700">
                      {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Link érvényessége (opcionális)</label>
                  <input type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={inputCls} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Műveletsáv */}
        <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost mr-auto">{t('common.close') || 'Bezárás'}</button>
            {tab === 'email' ? (
              <button type="button" onClick={handleSend} disabled={isSending} className="crm-btn crm-btn-primary">
                <PaperAirplaneIcon className="w-4 h-4 mr-1.5" />{isSending ? 'Küldés…' : 'Küldés az ügyfélnek'}
              </button>
            ) : (
              <button type="button" onClick={handleSaveLink} disabled={isSaving} className="crm-btn crm-btn-primary">
                <LinkIcon className="w-4 h-4 mr-1.5" />{isSaving ? 'Mentés…' : shareLink ? 'Link frissítése' : 'Link létrehozása'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareQuotationModal;
